import { Icon } from "@iconify/react";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createEventLocationType,
  updateEventLocationType,
  getEventLocationTypes
} from "../redux/eventLocation/eventLocationSlice";
import { toast } from "react-toastify";
import { truncateText } from "../helper/helper";
import useDebounce from "../hook/useDebounce";
import { useLocationFilter } from "../../src/context/LocationContext";
import PaginationHelper from "../helper/PaginationHelper";

const LocationTypeLayer = () => {

  const dispatch = useDispatch();
  const { selectedLocation } = useLocationFilter();

  const { locationTypes = [], locations = [], total = 0 } = useSelector(
    (state) => state.eventLocation
  );
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [locationId, setLocationId] = useState("");

  const [editId, setEditId] = useState("");
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editLocationId, setEditLocationId] = useState("");

  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const debouncedSearch = useDebounce(search, 1000);

  useEffect(() => {
    dispatch(
      getEventLocationTypes({
        page: currentPage,
        limit: entries,
        search: debouncedSearch,
        locationId: selectedLocation?.value || "",
      })
    );
  }, [dispatch, currentPage, entries, debouncedSearch, selectedLocation]);

  const totalEntries = total;
  const totalPages = Math.ceil(totalEntries / entries) || 1;

  const handleCreate = () => {

    if (!locationId || !name || !description) {
      toast.error("All fields required");
      return;
    }

    dispatch(
      createEventLocationType({
        locationId: locationId,
        name,
        description
      })
    )
      .unwrap()
      .then((res) => {

        toast.success(res.message || "Location Type Created");

        setName("");
        setDescription("");
        setLocationId("");

        dispatch(getEventLocationTypes());

        document.querySelector("#addLocationTypeModal .btn-close")?.click();
      })
      .catch((err) => toast.error(err.message));

  };

  const openEditModal = (item) => {
    setEditId(item.id);
    setEditName(item.name);
    setEditDescription(item.description);
    setEditLocationId(item.eventLocationId);
  };

  const handleUpdate = () => {

    if (!editLocationId || !editName || !editDescription) {
      toast.error("All fields required");
      return;
    }

    dispatch(
      updateEventLocationType({
        eventTypeId: editId,
        data: {
          eventLocationId: editLocationId,
          name: editName,
          description: editDescription
        }
      })
    )
      .unwrap()
      .then((res) => {

        toast.success(res.message || "Updated Successfully");

        dispatch(getEventLocationTypes());

        document.querySelector("#editLocationTypeModal .btn-close")?.click();
      })
      .catch((err) => toast.error(err.message));

  };

  return (
    <>
      <div className="card h-100 p-0 radius-12">

        <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between">

          <div className="d-flex align-items-center gap-3">

            <span>Show</span>

            <select
              className="form-select form-select-sm w-auto"
              value={entries}
              onChange={(e) => {
                setEntries(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="150">150</option>
              <option value="200">200</option>
            </select>

            <input
              className="form-control w-auto"
              placeholder="Search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />

          </div>

          <button
            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            data-bs-toggle="modal"
            data-bs-target="#addLocationTypeModal"
          >
            <Icon icon="ic:baseline-plus" /> Add Session Type
          </button>

        </div>

        <div className="card-body p-12">

          <div className="table-responsive">

            <table className="table bordered-table">

              <thead>
                <tr>
                  <th>S.L</th>
                  <th>Event Location</th>
                  <th>Session Type</th>
                  <th>Description</th>
                  <th>Create Date</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>

                {locationTypes.map((item, index) => (
                  <tr key={item.id}>
                    <td>{(currentPage - 1) * entries + index + 1}</td>

                    <td>
                      {truncateText(locations.find((l) => l.id === item.eventLocationId)?.name)}
                    </td>

                    <td>{truncateText(item.name, 20)}</td>

                    <td>{truncateText(item.description)}</td>

                    <td>{new Date(item.createdAt).toLocaleDateString()}</td>

                    <td className="text-center">

                      <button
                        className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        data-bs-toggle="modal"
                        data-bs-target="#editLocationTypeModal"
                        onClick={() => openEditModal(item)}
                      >
                        <Icon icon="lucide:edit" />
                      </button>

                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>
          {/* PAGINATION */}
          <PaginationHelper
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={total}
            pageSize={entries}
          />

        </div>

      </div>

      {/* ADD MODAL */}

      <div className="modal fade" id="addLocationTypeModal">

        <div className="modal-dialog modal-lg modal-dialog-centered">

          <div className="modal-content">

            <div className="modal-header">
              <h5>Add Session Type</h5>
              <button className="btn-close" data-bs-dismiss="modal" />
            </div>

            <div className="modal-body">
              <label className="form-label">Event Location <span className="text-danger">*</span></label>
              <select
                className="form-select mb-3"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
              >
                <option value="">Select Event Location</option>

                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}

              </select>
              <label className="form-label">Session Type<span className="text-danger">*</span> <small className="fst-italic">(Max 100 chars)</small></label>
              <input
                className="form-control mb-3"
                placeholder="Enter session type"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <label className="form-label">Description</label>
              <textarea
                className="form-control mb-3"
                placeholder="Enter session type description"
                rows="4"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />

              <button className="btn btn-primary" onClick={handleCreate}>
                Create
              </button>

            </div>

          </div>

        </div>

      </div>

      {/* EDIT MODAL */}

      <div className="modal fade" id="editLocationTypeModal">

        <div className="modal-dialog modal-lg modal-dialog-centered">

          <div className="modal-content">

            <div className="modal-header">
              <h5>Edit Location Type</h5>
              <button className="btn-close" data-bs-dismiss="modal" />
            </div>

            <div className="modal-body">
              <label className="form-label">Event Location <span className="text-danger">*</span></label>
              <select
                className="form-select mb-3"
                value={editLocationId}
                onChange={(e) => setEditLocationId(e.target.value)}
              >
                <option value="">Select Location</option>

                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}

              </select>
              <label className="form-label">Session Type<span className="text-danger">*</span> <small className="fst-italic">(Max 100 chars)</small></label>
              <input
                className="form-control mb-3"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
              <label className="form-label">Description</label>
              <textarea
                className="form-control mb-3"
                rows="4"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
              />

              <button className="btn btn-primary" onClick={handleUpdate}>
                Update
              </button>

            </div>

          </div>

        </div>

      </div>

    </>
  );
};

export default LocationTypeLayer;